import { WarningAttachmentsCanNotBeDeleted } from 'owa-locstrings/lib/strings/warningattachmentscannotbedeleted.locstring.json';
import loc, { format } from 'owa-localize';
import removeInfoBarMessage from 'owa-info-bar/lib/actions/removeInfoBarMessage';
import trySaveMessage, { waitForActiveSaving } from '../actions/trySaveMessage';
import { SMIME_CONTROL_ERROR_MESSAGE_ID } from '../orchestrators/onSmimeOptionsChangeOrchestrator';
import { findComposePlugins } from '../plugins/ComposePlugins';
import { ComposeViewState } from 'owa-mail-compose-store';
import createAttachments from '../utils/createAttachments';
import { infoBarAddHandler } from '../utils/infoBarHandlers';
import isInlineAttachmentStateType from 'owa-attachment-full-data/lib/utils/isInlineAttachmentStateType';
import getAttachment from 'owa-attachment-model-store/lib/selectors/getAttachment';
import { AttachmentState, AttachmentType } from 'owa-attachment-well-data';
import removeAttachmentFromWell from 'owa-attachment-well-data/lib/actions/removeAttachmentFromWell';
import updateChangeKeyOnItemId from 'owa-attachment-well-data/lib/actions/updateChangeKeyOnItemId';
import deleteAttachment from 'owa-attachment-well-data/lib/services/deleteAttachment';
import { ClientItemId } from 'owa-client-ids';
import createBlob from 'owa-encoding-utils/lib/utils/base64/createBlob';
import encode from 'owa-encoding-utils/lib/utils/base64/encode';
import blobToFile from 'owa-encoding-utils/lib/utils/blobToFile';
import { checkIfResponseSuccess, getComposeItemResponseShape } from 'owa-mail-store';
import getItem from 'owa-mail-store/lib/services/getItem';
import DeleteAttachmentResponseMessage from 'owa-service/lib/contract/DeleteAttachmentResponseMessage';
import ItemInfoResponseMessage from 'owa-service/lib/contract/ItemInfoResponseMessage';
import getSmimeAttachmentType from 'owa-smime/lib/utils/getSmimeAttachmentType';

import { trace } from 'owa-trace';
import {
    ClientAttachmentId,
    SmimeAttachmentType,
    isSmimeAttachment,
    getTypeOfAttachment,
    TypeOfAttachment,
} from 'owa-attachment-model-store';
import {
    createLocalComputerFiles,
    createMailItemFileForSmime,
    AttachmentFileList,
} from 'owa-attachment-file-types';
import {
    SMIME_INSTALLED_HEADER_KEY,
    SMIME_INSTALLED_HEADER_TRUE,
} from 'owa-smime/lib/utils/constants';

const MAIL_FILE_EXTENSION = '.eml';

export default async function onSmimeModeDisabled(viewState: ComposeViewState) {
    // Remove if any control error infobars are present.
    removeInfoBarMessage(viewState, SMIME_CONTROL_ERROR_MESSAGE_ID);

    if (viewState.itemId) {
        // Check and remove p7m file if its a switch from S/MIME message to non-S/MIME message
        // and item is already created. We await this to ensure that the p7m attachment is deleted before the action is completed
        await tryDeleteSmimeAttachmentType(viewState);
        moveAttachmentsFromClientToServer(viewState);
    } else if (!(await waitForActiveSaving(viewState))) {
        // If we don't have itemId it means a save has never been done, so before we start uploading attachments we need to do a save action.
        await trySaveMessage(viewState);
    }
}

/**
 * For every S/MIME attachment, we should remove it from the well and create it again
 * using the upload queue once S/MIME is disabled
 * @param viewState
 */
function moveAttachmentsFromClientToServer(viewState: ComposeViewState): void {
    const attachments: AttachmentState[] = [
        ...viewState.attachmentWell.docViewAttachments,
        ...viewState.attachmentWell.inlineAttachments,
    ];
    attachments.forEach(async attachment => {
        const attachmentId = attachment.attachmentId.Id;
        const attachmentModel = getAttachment(attachment.attachmentId);
        if (attachmentModel && isSmimeAttachment(attachmentId)) {
            const smimeAttachment = attachmentModel.model as SmimeAttachmentType;
            if (!smimeAttachment) {
                trace.error(
                    'SmimeTraceError: Attachment not found in moveAttachmentsFromClientToServer'
                );
                return;
            }
            const attachmentType = getTypeOfAttachment(smimeAttachment);
            let changedFiles: AttachmentFileList;
            if (attachmentType === TypeOfAttachment.Mail) {
                changedFiles = createMailItemFileForSmime(
                    smimeAttachment.Name,
                    encode(smimeAttachment.MimeContent),
                    smimeAttachment.Size
                );
            } else {
                const file = createFileFromSmimeAttachment(smimeAttachment);
                changedFiles = createLocalComputerFiles([file]);
            }
            // Remove existing attachments from the well
            removeAttachmentFromWell(viewState.attachmentWell, attachment);
            // Recreate the same attachments
            if (isInlineAttachmentStateType(attachment)) {
                createAttachments(
                    changedFiles,
                    viewState,
                    {
                        isInline: true,
                        isHiddenInline: false,
                        shouldShare: false,
                        skipProcessInlineImage: true /* Since image is already present from S/MIME mode, we need not process it again */,
                    },
                    null /* processInlineImageBeforeUpload */,
                    null /* infoBarAddHandler */,
                    null /* infoBarRemoveHandler */,
                    null /* onFinish */,
                    (
                        parentItemId: ClientItemId,
                        attachmentId: ClientAttachmentId,
                        attachmentType: AttachmentType
                    ) =>
                        onAttachmentCreated(
                            attachmentId,
                            attachmentType,
                            smimeAttachment.ContentId,
                            viewState
                        )
                );
            } else {
                createAttachments(
                    changedFiles,
                    viewState,
                    {
                        isInline: false,
                        isHiddenInline: true,
                        shouldShare: false,
                    },
                    null /* processInlineImageBeforeUpload */,
                    null /* infoBarAddHandler */,
                    null /* infoBarRemoveHandler */
                );
            }
        }
    });
}

function onAttachmentCreated(
    attachmentIdFromServer: ClientAttachmentId,
    attachmentType: AttachmentType,
    oldContentId: string,
    viewState: ComposeViewState
) {
    const plugins = findComposePlugins(viewState);
    if (!plugins) {
        trace.error('SmimeTraceError: Plugin not found on Smime mode disabled');
        return;
    }
    const { inlineImage, contentOperation } = plugins;
    const isElementReplaced = inlineImage.onSmimeUploadSuccess(
        oldContentId,
        attachmentIdFromServer,
        attachmentType.ContentId
    );
    isElementReplaced && contentOperation.updateContentToViewState();
}

function createFileFromSmimeAttachment(smimeAttachment: SmimeAttachmentType) {
    const { Name, ContentType, MimeContent, Content } = smimeAttachment;
    const nameWithExtension = MimeContent ? Name + MAIL_FILE_EXTENSION : Name;
    const fileContent = MimeContent ? encode(MimeContent) : Content;
    const blob = createBlob(fileContent, ContentType);
    return blobToFile(blob, nameWithExtension);
}

// Delete the S/MIME attachment type (p7m file), if present in the item
async function tryDeleteSmimeAttachmentType(viewState: ComposeViewState) {
    const { itemId } = viewState;
    const shape = getComposeItemResponseShape(viewState.bodyType);
    try {
        const smimeHeaders = new Headers();
        smimeHeaders.set(SMIME_INSTALLED_HEADER_KEY, SMIME_INSTALLED_HEADER_TRUE);
        const requestOptions = {
            headers: smimeHeaders,
        };
        const itemResponse = await getItem([itemId.Id], shape, 'MailCompose', null, requestOptions);
        if (checkIfResponseSuccess(itemResponse[0])) {
            const itemInfoResponseMessage = itemResponse[0] as ItemInfoResponseMessage;
            const item = itemInfoResponseMessage.Items[0];
            const smimeAttachment: AttachmentType = getSmimeAttachmentType(item);

            if (item && smimeAttachment && smimeAttachment.AttachmentId) {
                const attachmentId = {
                    Id: smimeAttachment.AttachmentId.Id,
                } as ClientAttachmentId;
                const deleteAttachmentResponse = await deleteAttachment(
                    attachmentId,
                    requestOptions
                );
                const deleteAttachmentResponseMessage: DeleteAttachmentResponseMessage =
                    deleteAttachmentResponse.ResponseMessages.Items[0];
                if (deleteAttachmentResponseMessage.ResponseClass === 'Success') {
                    updateChangeKeyOnItemId(
                        itemId,
                        deleteAttachmentResponseMessage.RootItemId.RootItemChangeKey
                    );
                } else {
                    throw new Error(
                        `Failed to delete p7m file with ResponseClass= ${
                            deleteAttachmentResponseMessage.ResponseClass
                        }, ResponseCode=${deleteAttachmentResponseMessage.ResponseCode}`
                    );
                }
            }
        }
    } catch (error) {
        trace.error('SmimeTraceError: ' + error.message);
        infoBarAddHandler(viewState)('WarningAttachmentCanNotBeDeleted', [
            format(loc(WarningAttachmentsCanNotBeDeleted), 'smime.p7m'),
        ]);
    }
}
